/**
 * Centralized Route Configuration
 *
 * Single source of truth for all route paths, ACL slugs, sidebar items,
 * profile menu items, and active-menu mappings.
 *
 * Every consumer (App.tsx, ProtectedRoute, Layout, layoutHelpers)
 * reads from this file instead of maintaining its own maps.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RouteEntry {
  /** Unique identifier, e.g. 'dashboard', 'se-metrics' */
  id: string
  /** URL path, e.g. '/', '/se-metrics' */
  path: string
  /** ACL slug used for both route protection and sidebar visibility */
  pageSlug: string
  /** Whether the route is gated by the GrowthBook ACL. Defaults to true. */
  requiresAccess?: boolean
  /** Whether this route appears in the sidebar / header tabs */
  showInNav?: boolean
  /** Where the nav item appears: 'header' (top tabs) or 'sidebar' (left panel) */
  navLocation?: 'header' | 'sidebar'
  /** Label shown in sidebar / header tabs */
  label?: string
  /** Icon name for sidebar */
  icon?: string
}

export interface ProfileMenuItem {
  /** Unique identifier, e.g. 'faqs', 'settings' */
  id: string
  /** Display title */
  title: string
  /** Icon name */
  icon: string
  /** ACL slug that controls visibility */
  slug: string
  /** Navigation path when clicked */
  path: string
  /** Whether the route should open in a new browser tab */
  openInNewTab?: boolean
}

// ---------------------------------------------------------------------------
// Route Definitions
// ---------------------------------------------------------------------------

export const ASK_DEVLENS_AVAILABILITY_FLAG = 'ask-devlens-chatbot-available'
export const ASK_DEVLENS_PAGE_SLUG = 'page:ask-devlens'

export const ROUTES: RouteEntry[] = [
  {
    id: 'dashboard',
    path: '/',
    pageSlug: 'page:dashboard',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'Home',
    icon: 'home',
  },
  {
    id: 'programs-teams',
    path: '/programs-teams',
    pageSlug: 'page:programs-teams',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'Programs & Teams',
    icon: 'account_tree',
  },
  {
    id: 'se-metrics',
    path: '/se-metrics',
    pageSlug: 'page:se-metrics',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'SE Metrics',
    icon: 'timeline',
  },
  {
    id: 'ai-productivity',
    path: '/ai-productivity',
    pageSlug: 'page:ai-productivity',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'AI Productivity',
    icon: 'track_changes',
  },
  {
    id: 'vendor-analysis',
    path: '/vendor-analysis',
    pageSlug: 'page:vendor-analysis',
    showInNav: false,
    navLocation: 'sidebar',
    label: 'Vendor Analysis',
    icon: 'analytics',
  },
  {
    id: 'vendor-contribution-insights',
    path: '/vendor-contribution-insights',
    pageSlug: 'page:vendor-contribution-insights',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'Vendor Contribution Insights',
    icon: 'insights',
  },
  {
    id: 'reports',
    path: '/reports',
    pageSlug: 'page:reports',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'Reports',
    icon: 'insert_chart',
  },
  {
    id: 'custom-report',
    path: '/reports/custom',
    pageSlug: 'page:reports',
  },
  {
    id: 'custom-report-generate',
    path: '/reports/custom/generate',
    pageSlug: 'page:reports',
  },
  {
    id: 'engineering-leadership-insights',
    path: '/engineering-leadership-insights',
    pageSlug: 'page:engineering-leadership-insights',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'Leadership Insight',
    icon: 'leaderboard',
  },
  {
    id: 'commit-analytics',
    path: '/commit-analytics',
    pageSlug: 'page:commit-analytics',
  },
  {
    id: 'programs-configurations',
    path: '/programs-configurations',
    pageSlug: 'page:programs-configurations',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'Programs & Configurations',
    icon: 'integration_instructions',
  },
  {
    id: 'agile-metrics',
    path: '/agile-metrics',
    pageSlug: 'page:agile-metrics',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'Agile Metrics',
    icon: 'query_stats',
  },
  {
    id: 'manage-tag',
    path: '/manage-tag',
    pageSlug: 'page:manage-tag',
    label: 'Manage Licenses & Tag',
    icon: 'supervised_user_circle',
  },
  {
    id: 'docs',
    path: '/docs',
    pageSlug: 'page:docs',
  },
  {
    id: 'faqs',
    path: '/faqs',
    pageSlug: 'page:faqs',
  },
  {
    id: 'metrics-settings',
    path: '/metrics-settings',
    pageSlug: 'page:metrics-settings',
  },
  {
    id: 'manage-roles',
    path: '/manage-roles',
    pageSlug: 'page:manage-roles',
  },
  {
    id: 'ask-devlens',
    path: '/ask-devlens',
    pageSlug: ASK_DEVLENS_PAGE_SLUG,
  },
  {
    id: 'ask-devlens-poc',
    path: '/ask-devlens-poc',
    pageSlug: 'page:ask-devlens-poc',
  },
  {
    id: 'ask-devlens-v2',
    path: '/ask-devlens-v2',
    pageSlug: 'page:ask-devlens',
  },
  // Dashboard sub-routes (inherit page:dashboard slug)
  {
    id: 'program-team',
    path: '/program/:programId/team/:id',
    pageSlug: 'page:dashboard',
  },
  {
    id: 'team-legacy',
    path: '/team/:id',
    pageSlug: 'page:dashboard',
  },
  {
    id: 'program',
    path: '/program/:id',
    pageSlug: 'page:dashboard',
  },
  {
    id: 'se-metrics-trends',
    path: '/se-trends',
    pageSlug: 'page:se-metrics-trends',
    showInNav: true,
    navLocation: 'sidebar',
    label: 'SE Metrics Trends',
    icon: 'stacked_line_chart',
  },
]

// ---------------------------------------------------------------------------
// Profile Menu Items
// ---------------------------------------------------------------------------

export const PROFILE_MENU_ITEMS: ProfileMenuItem[] = [
  {
    id: 'docs',
    title: 'Docs',
    icon: 'description',
    slug: 'page:docs',
    path: '/docs',
    openInNewTab: true,
  },
  {id: 'faqs', title: 'FAQs', icon: 'list_alt', slug: 'page:faqs', path: '/faqs'},
  {id: 'ask-devlens', title: 'Ask DevLens', icon: 'smart_toy', slug: ASK_DEVLENS_PAGE_SLUG, path: '/ask-devlens'},
  {
    id: 'manage-tag',
    title: 'Manage Access',
    icon: 'supervised_user_circle',
    slug: 'page:manage-tag',
    path: '/manage-access',
  },
  {
    id: 'metrics-settings',
    title: 'Metrics Settings',
    icon: 'settings',
    slug: 'page:metrics-settings',
    path: '/metrics-settings',
  },
  {
    id: 'manage-roles',
    title: 'Manage Roles & Responsibilities',
    icon: 'supervised_user_circle',
    slug: 'page:manage-roles',
    path: '/manage-roles',
  },
]

// ---------------------------------------------------------------------------
// Derived lookups (computed once at import time)
// ---------------------------------------------------------------------------

/** Routes that appear in the sidebar */
export const SIDEBAR_ROUTES = ROUTES.filter(r => r.showInNav)

/** Routes shown in the top header tabs */
export const HEADER_NAV_ROUTES = ROUTES.filter(r => r.showInNav && r.navLocation === 'header')

/** Routes shown in the left sidebar */
export const SIDEBAR_NAV_ROUTES = ROUTES.filter(r => r.showInNav && r.navLocation === 'sidebar')

/**
 * Map path prefix → pageSlug for route-level access checks.
 * The index route '/' is handled as the fallback.
 */
export const PATH_TO_PAGE_SLUG: Record<string, string> = Object.fromEntries(
  ROUTES.filter(r => r.path !== '/' && !r.path.includes(':')).map(r => [r.path, r.pageSlug]),
)

/**
 * Resolve the ACL page slug for a given pathname.
 * Falls back to 'page:dashboard' for the index route and unmatched paths.
 */
export const getPageSlugForPath = (pathname: string): string => {
  for (const [prefix, slug] of Object.entries(PATH_TO_PAGE_SLUG)) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return slug
    }
  }
  return 'page:dashboard'
}

/**
 * Whether the matched route is gated by the GrowthBook ACL.
 * Routes are access-controlled unless they explicitly opt out.
 */
export const isAccessRequiredForPath = (pathname: string): boolean => {
  const route = ROUTES.find(candidate => {
    if (candidate.path === '/' || candidate.path.includes(':')) return false
    return pathname === candidate.path || pathname.startsWith(`${candidate.path}/`)
  })

  return route?.requiresAccess !== false
}

/**
 * Resolve the active sidebar menu ID from a pathname.
 */
export const getActiveMenuId = (pathname: string): string => {
  for (const route of SIDEBAR_ROUTES) {
    if (route.path === '/') {
      if (pathname === '/') {
        return route.id
      }
    } else if (pathname.startsWith(route.path) || pathname.startsWith(`${route.path}/`)) {
      return route.id
    }
  }
  return ''
}
